// const CONTENT_ID = 'page-document';

// Novo validador usando axe-core
// Mantemos o formato de retorno: array de grupos { title, errors: [{ msg, el }] }
// Regras originais (imagens alt, títulos em ordem, elementos vazios) agora serão
// cobertas em parte por axe-core. Podemos opcionalmente complementar com as
// verificações existentes se desejado (feature flag futura).

import checkEmptyTag from './checkEmptyTag';
// import checkImageAlt from './checkImageAlt';

// axe-core é carregado sob demanda para não entrar no bundle principal.
// São ~960 KB que só servem para editores logados — não faz sentido que
// todos os visitantes paguem esse custo de download.
let _axe = null;
let _axeLocalePtBR = null;

const loadAxe = async () => {
  if (!_axe) {
    const [axeModule, localeModule] = await Promise.all([
      import(/* webpackChunkName: "axe-core" */ 'axe-core'),
      import(/* webpackChunkName: "axe-core" */ 'axe-core/locales/pt_BR.json'),
    ]);
    _axe = axeModule.default || axeModule;
    _axeLocalePtBR = localeModule.default || localeModule;
  }
  return { axe: _axe, axeLocalePtBR: _axeLocalePtBR };
};

// Traduções básicas (placeholder). Poderá ser expandido posteriormente.
const impactMap = {
  minor: 'Menor',
  moderate: 'Moderado',
  serious: 'Grave',
  critical: 'Crítico',
};

// O axe é configurado uma única vez: cada chamada de `configure` reconstrói toda
// a auditoria (mais de 100 regras), o que é desperdício a cada execução.
let axeConfigured = false;

const buildHiddenRulesConfig = (axe) => {
  if (typeof axe.getRules !== 'function') return null;

  try {
    const rules = axe.getRules();
    if (!Array.isArray(rules) || !rules.length) return null;

    return rules.map((rule) => ({
      id: rule.ruleId || rule.id,
      // A regra empty-heading precisa respeitar a visibilidade para não acusar
      // títulos de acordeons fechados cujo texto está oculto junto com o painel.
      excludeHidden: (rule.ruleId || rule.id) === 'empty-heading',
      ...((rule.ruleId || rule.id) === 'link-name'
        ? {
            any: [
              'has-visible-text',
              'aria-label',
              'aria-labelledby',
              { id: 'non-empty-title', options: { attribute: 'title' } },
              'hidden-link-name',
            ],
          }
        : {}),
    }));
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[AccessibilityChecker] Falha ao configurar regras axe para incluir elementos ocultos:',
        error,
      );
    }
    return null;
  }
};

// `excludeHidden: false` faz o axe auditar abas e sanfonas fechadas sem que seja
// necessário exibi-las. Forçar a exibição obrigava o navegador a renderizar
// (estilo, layout, pintura e hit testing) todo o conteúdo oculto da página, o que
// multiplicava por 3 a 4 o tempo da checagem em páginas com muitas abas.
const configureAxe = (axe, axeLocalePtBR) => {
  if (axeConfigured) return;
  axeConfigured = true;

  const config = {
    locale: axeLocalePtBR,
    checks: [
      {
        id: 'hidden-link-name',
        metadata: {
          messages: {
            pass: 'O link oculto possui nome acessível',
            fail: 'O link oculto não possui nome acessível',
          },
        },
        evaluate: (node, options, virtualNode) =>
          !axe.commons.dom.isVisibleToScreenReaders(virtualNode) &&
          Boolean(
            collapseWhitespace(
              axe.commons.text.accessibleTextVirtual(virtualNode, {
                includeHidden: true,
              }),
            ),
          ),
      },
    ],
  };
  const rules = buildHiddenRulesConfig(axe);
  if (rules) {
    config.rules = rules;
  }

  axe.configure(config);
};

const collapseWhitespace = (value = '') =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

let htmlParserTemplate = null;

const htmlToPlainText = (html) => {
  if (!html || typeof html !== 'string') return '';

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return collapseWhitespace(html.replace(/<[^>]+>/g, ' '));
  }

  // O conteúdo de um <template> é inerte, então extrair o texto do HTML da
  // violação não dispara requisições de imagens/iframes (um <div> dispararia).
  if (!htmlParserTemplate) {
    htmlParserTemplate = document.createElement('template');
  }
  htmlParserTemplate.innerHTML = html;
  const text = htmlParserTemplate.content.textContent || '';
  htmlParserTemplate.innerHTML = '';

  return collapseWhitespace(text);
};

const guessElementLabel = (element, targetSelector = '') => {
  let tagName = '';
  if (element?.tagName) {
    tagName = element.tagName.toLowerCase();
  } else if (targetSelector) {
    const match = targetSelector.match(/[a-zA-Z0-9-]+/);
    tagName = match ? match[0].toLowerCase() : '';
  }

  if (!tagName) return '<elemento>';
  if (tagName === 'img') return '<imagem>';
  if (tagName === 'svg') return '<svg>';
  if (tagName === 'a') return '<link>';
  return `<${tagName}>`;
};

const deriveNodeDisplayText = (node, element) => {
  const htmlText = htmlToPlainText(node?.html);
  if (htmlText) return htmlText;

  const elementText = collapseWhitespace(element?.textContent || '');
  if (elementText) return elementText;

  const targetSelector = Array.isArray(node?.target)
    ? node.target[0]
    : node?.target;
  return guessElementLabel(element, targetSelector);
};

// Função para adaptar violações do axe ao formato usado pelo componente.
const mapAxeViolations = (violations, root = document) => {
  return violations.map((v) => {
    const errors = v.nodes.map((n) => {
      // Pega o primeiro seletor alvo para localizar elemento.
      let el = null;
      if (n.target && n.target.length) {
        try {
          el = root.querySelector(n.target[0]);
        } catch (e) {
          // Ignora erros de querySelector (ex: seletor complexo não suportado)
          el = null;
        }
      }
      const fallbackSummary =
        n.failureSummary || v.help || v.description || impactMap[v.impact];

      const displayText = deriveNodeDisplayText(n, el);
      return {
        msg: fallbackSummary || '',
        failureSummary: n.failureSummary || '',
        el,
        impact: v.impact,
        rule: v.id,
        displayText,
        html: n.html,
        target: Array.isArray(n.target) ? [...n.target] : n.target,
        all: n.all,
        any: n.any,
        none: n.none,
      };
    });
    return {
      title: v.help || v.description || v.id,
      ruleId: v.id,
      impact: v.impact,
      description: v.description,
      help: v.help,
      helpUrl: v.helpUrl,
      errors,
    };
  });
};

const isHiddenElement = (element) => {
  if (!element) return false;

  if (
    element.closest('[aria-hidden="true"], [hidden]') ||
    element.closest('[style*="display: none"], [style*="visibility: hidden"]')
  ) {
    return true;
  }

  return (
    typeof window !== 'undefined' &&
    getComputedStyle(element).display === 'none'
  );
};

const checkTabPanelsHeadingOrder = (content) => {
  const errors = [];
  const reportedHeadings = new Set();
  const contexts = [
    ...content.querySelectorAll('[role="tabpanel"]'),
    ...content.querySelectorAll('.accordion .content'),
  ];

  for (const panel of contexts) {
    const firstHeading = panel.querySelector('h1,h2,h3,h4,h5,h6');
    if (!firstHeading) continue;

    const level = Number(firstHeading.tagName.slice(1));
    if (level <= 2) continue;

    reportedHeadings.add(firstHeading);
    errors.push({
      msg: 'Hierarquia de títulos inválida',
      failureSummary:
        'Corrija qualquer um dos itens a seguir:\n  Hierarquia de títulos inválida',
      el: firstHeading,
      impact: 'moderate',
      rule: 'heading-order',
      displayText:
        collapseWhitespace(firstHeading.textContent) ||
        `<${firstHeading.tagName.toLowerCase()}>`,
      html: firstHeading.outerHTML,
      target: [],
    });
  }

  const headings = [...content.querySelectorAll('h1,h2,h3,h4,h5,h6')];
  headings.forEach((heading, index) => {
    const nextHeading = headings[index + 1];
    if (!nextHeading) return;

    const level = Number(heading.tagName.slice(1));
    const nextLevel = Number(nextHeading.tagName.slice(1));
    if (nextLevel >= level || reportedHeadings.has(heading)) return;

    reportedHeadings.add(heading);
    errors.push({
      msg: 'Hierarquia de títulos inválida',
      failureSummary:
        'Corrija qualquer um dos itens a seguir:\n  Hierarquia de títulos inválida',
      el: heading,
      impact: 'moderate',
      rule: 'heading-order',
      displayText:
        collapseWhitespace(heading.textContent) ||
        `<${heading.tagName.toLowerCase()}>`,
      html: heading.outerHTML,
      target: [],
    });
  });

  if (!errors.length) return [];

  return [
    {
      title: 'Níveis dos títulos devem aumentar de um em um',
      ruleId: 'heading-order',
      impact: 'moderate',
      description:
        'Certifique-se de que a hierarquia dos níveis de títulos seja semanticamente correta',
      help: 'Níveis dos títulos devem aumentar de um em um',
      errors,
    },
  ];
};

const checkEmptyTags = (content, axeResults) => {
  const report = [];
  checkEmptyTag(content, report);

  const emptyHeadingNodes = new Set(
    (axeResults?.violations || [])
      .filter((violation) => violation.id === 'empty-heading')
      .flatMap((violation) =>
        violation.nodes
          .map((node) => node.target?.[0])
          .filter(Boolean)
          .map((selector) => {
            try {
              return content.querySelector(selector);
            } catch (error) {
              return null;
            }
          }),
      )
      .filter(Boolean),
  );

  return report
    .map((group) => ({
      ...group,
      errors: group.errors.filter((error) => !emptyHeadingNodes.has(error.el)),
    }))
    .filter((group) => group.errors.length);
};

// checkA11y agora é assíncrono.
const checkA11y = async (content) => {
  if (!content) return null;

  // Carrega axe-core sob demanda (só na primeira chamada; depois usa cache).
  const { axe, axeLocalePtBR } = await loadAxe();

  // Executa axe-core no escopo do conteúdo.
  let axeResults = null;
  try {
    configureAxe(axe, axeLocalePtBR);
    axeResults = await axe.run(content, {
      // Descartar os demais tipos de resultado evita que o axe monte seletores
      // e HTML para todos os nós aprovados, que são a maioria em páginas grandes.
      resultTypes: ['violations'],
    });
  } catch (e) {
    // Em caso de falha, retornamos um grupo indicando erro interno.
    return [
      {
        title: 'Erro interno no validador de acessibilidade',
        errors: [
          {
            msg: `Falha ao executar axe-core: ${e.message}`,
          },
        ],
      },
    ];
  }

  const report = [];

  // Mapeia violações do axe.
  if (axeResults?.violations?.length) {
    const mappedViolations = mapAxeViolations(axeResults.violations, content);
    report.push(
      ...mappedViolations.filter(
        (violation) =>
          violation.ruleId !== 'empty-heading' ||
          violation.errors.some((error) => !isHiddenElement(error.el)),
      ),
    );
  }

  report.push(...checkTabPanelsHeadingOrder(content));
  report.push(...checkEmptyTags(content, axeResults));

  // Complemento: mantemos regras customizadas locais para continuidade.
  // (Podemos remover depois ou tornar configurável.)
  // checkImageAlt(content, report);
  // checkEmptyTag(content, report);

  return report;
};

export default checkA11y;
